import React, { useState, useCallback, useEffect, useMemo } from "react";
import { fetchOrCreatePlayer, updatePlayer } from "../../services/players";
import toast from "react-hot-toast";

import PseudoStep from "components/onboarding/_steps/PseudoStep";
import AvatarStep from "components/onboarding/_steps/AvatarStep";
import EmailStep from "components/onboarding/_steps/EmailStep";
import RetakeQuiz from "components/onboarding/_steps/RetakeQuiz";
import Countdown from "components/onboarding/_steps/Countdown";
import ValidationStep from "components/onboarding/_steps/ValidationStep";
import QuizNotAvailable from "components/onboarding/_steps/QuizNotAvailable";
import AvatarAndPseudoStep from "components/onboarding/_steps/AvatarAndPseudoStep";
import IserbaAuthStep from "./iserba/IserbaAuthStep";
import ProgressMap from "components/progressMap/ProgressMap";

const SoloOnboarding = ({ quiz, quizWrapper, initialPlayer, navigate }) => {
	const [player, setPlayer] = useState(initialPlayer || null);
	const [playerValidated, setPlayerValidated] = useState(
		initialPlayer?.alreadyPlayed
	);
	const [avatarChangeRequested, setAvatarChangeRequested] = useState(false);
	const [playerChangeRequested, setPlayerChangeRequested] = useState(false);
	const [onboardingComplete, setOnboardingComplete] = useState(false);

	const quizOrganizationId = quiz.organization_id;

	const updatePlayerState = useCallback(
		(updatedPlayer) => {
			const newPlayer = {
				...player,
				...updatedPlayer,
				quizOrganizationId,
			};
			localStorage.setItem("player", JSON.stringify(newPlayer));
			setPlayer(newPlayer);
		},
		[player, quizOrganizationId]
	);

	const handlePlayerValidation = useCallback(async () => {
		if (player?.id) {
			await updatePlayer(player.id, player);
		}
		setPlayerValidated(true);
		setOnboardingComplete(true);
	}, [player]);

	useEffect(() => {
		console.log(
			new Date(quiz.accessible_from),
			new Date(quiz.accessible_until),
			new Date(),
			quiz.accessible_from && new Date(quiz.accessible_from) > new Date(),
			quiz.accessible_until && new Date(quiz.accessible_until) < new Date(),
			quiz.config.show_progress_map,
			playerValidated,
			player?.alreadyPlayed,
			onboardingComplete
		);
		if (quiz.accessible_from && new Date(quiz.accessible_from) > new Date())
			return;
		if (quiz.accessible_until && new Date(quiz.accessible_until) < new Date())
			return;

		if (
			quiz.config.show_progress_map &&
			playerValidated &&
			player?.alreadyPlayed
		)
			return;

		if (!onboardingComplete) return;

		console.log("Onboarding complete, navigating to play screen");
		navigate(`/play/${quiz.id}`);
	}, [onboardingComplete, quiz, navigate, player]);

	const handleStepChange = useMemo(
		() => ({
			email: async (email) => {
				const { data: newPlayer, error } = await fetchOrCreatePlayer(
					quiz.id,
					email
				);

				if (error) return toast.error("Erreur lors de la création du joueur.");
				setPlayerChangeRequested(false);
				updatePlayerState(newPlayer);
			},
			iserba: async (newPlayer) => {
				setPlayerChangeRequested(false);
				updatePlayerState(newPlayer);
			},
			pseudo: async (pseudo) => updatePlayerState({ pseudo }),
			avatar: async (avatar) => {
				setAvatarChangeRequested(false);
				updatePlayerState({ avatar });
			},
			avatarAndPseudo: async ({ pseudo, avatar }) => {
				setAvatarChangeRequested(false);
				updatePlayerState({ pseudo, avatar });
			},
		}),
		[quiz.id, updatePlayerState]
	);

	const steps = useMemo(
		() => [
			{
				condition: (p) =>
					quiz.auth_steps.includes("email") &&
					(!p?.email || playerChangeRequested),
				component: (
					<EmailStep key="email" onEmailChosen={handleStepChange.email} />
				),
			},
			{
				condition: (p) =>
					quiz.auth_steps.includes("iserba") && (!p || playerChangeRequested),
				component: (
					<IserbaAuthStep
						key="iserba"
						handlePlayerChosen={handleStepChange.iserba}
					/>
				),
			},
			{
				condition: (p) =>
					quiz.auth_steps.includes("avatar_and_pseudo") &&
					(!p?.pseudo || !p?.avatar),
				component: (
					<AvatarAndPseudoStep
						key="avatar-and-pseudo"
						quizOrganizationId={quizOrganizationId}
						player={player}
						onComplete={handleStepChange.avatarAndPseudo}
					/>
				),
			},
			{
				condition: (p) => quiz.auth_steps.includes("pseudo") && !p?.pseudo,
				component: (
					<PseudoStep key="pseudo" onPseudoChosen={handleStepChange.pseudo} />
				),
			},
			{
				condition: (p) =>
					(quiz.auth_steps.includes("avatar") && !p?.avatar) ||
					avatarChangeRequested,
				component: (
					<AvatarStep
						key="avatar"
						player={player}
						quizOrganizationId={quizOrganizationId}
						onAvatarChosen={handleStepChange.avatar}
					/>
				),
			},
			{
				condition: () => !playerValidated,
				component: (
					<ValidationStep
						key="validation"
						player={player}
						quizOrganizationId={quizOrganizationId}
						onValidatePlayer={handlePlayerValidation}
						onChangePlayer={() => setPlayerChangeRequested(true)}
						onEditAvatar={() => setAvatarChangeRequested(true)}
					/>
				),
			},
		],
		[
			quiz,
			player,
			playerChangeRequested,
			avatarChangeRequested,
			handleStepChange,
			playerValidated,
			handlePlayerValidation,
			quizOrganizationId,
		]
	);

	const currentStep = useMemo(
		() => steps.find((step) => step.condition(player)),
		[steps, player, playerValidated]
	);
	if (currentStep) return currentStep.component;

	if (
		quiz.config.show_progress_map &&
		(player?.alreadyPlayed ||
			(quiz.accessible_from && new Date(quiz.accessible_from) > new Date()) ||
			(quiz.accessible_until && new Date(quiz.accessible_until) < new Date()))
	)
		return (
			<ProgressMap
				quiz={quiz}
				handleReplay={() => navigate(`/play/${quiz.id}`)}
				handleVerifyPlayer={() => setPlayerValidated(false)}
				playerAlreadyPlayed={player?.alreadyPlayed}
			/>
		);
	if (quiz.accessible_from && new Date(quiz.accessible_from) > new Date())
		return <Countdown targetDate={quiz.accessible_from} />;
	if (quiz.accessible_until && new Date(quiz.accessible_until) < new Date())
		return <QuizNotAvailable />;
	if (player?.alreadyPlayed)
		return <RetakeQuiz onRetake={() => navigate(`/play/${quiz.id}`)} />;

	return null;
};

export default SoloOnboarding;
